export interface RuntimeConfigEnv {
  VITE_GENERATION_API_BASE_URL?: string;
  VITE_ALLOW_LOCAL_API_IN_PRODUCTION?: string | boolean;
  VITE_DEPLOYMENT_REGION?: string;
}

export type DeploymentRegion = "CN" | "GLOBAL";
export type PaymentChannel = "wechat_pay" | "stripe";

export interface RuntimeMarketConfig {
  region: DeploymentRegion;
  paymentChannel: PaymentChannel;
  paymentProviderLabel: string;
  showWechatLogin: boolean;
  showGoogleLogin: boolean;
  showIcp: boolean;
  isChinaVersion: boolean;
  isInternationalVersion: boolean;
  loginPath: string;
  generatePath: string;
}

export interface RuntimeConfig {
  apiBaseUrl: string;
  publicApiBaseUrl: string;
  allowLocalApiInProduction: boolean;
  isProduction: boolean;
  market: RuntimeMarketConfig;
}

export interface RuntimeConfigValidation {
  ok: boolean;
  errors: string[];
}

export const defaultGlobalApiBaseUrl = "https://api.pet.autumnsoft.net";
export const defaultChinaApiBaseUrl = "https://api.pet.autumnsoft.cn";
export const defaultApiBaseUrl = defaultGlobalApiBaseUrl;
export const runtimeConfig = createRuntimeConfig(import.meta.env, import.meta.env.PROD, getBrowserLocation());

export function createRuntimeConfig(
  env: RuntimeConfigEnv,
  isProduction: boolean,
  browserLocation = ""
): RuntimeConfig {
  const market = detectMarket(browserLocation, env.VITE_DEPLOYMENT_REGION);
  const apiBaseUrl = normalizeBaseUrl(readString(env.VITE_GENERATION_API_BASE_URL) || defaultApiBaseUrlForMarket(market.region));
  const allowLocalApiInProduction = readBoolean(env.VITE_ALLOW_LOCAL_API_IN_PRODUCTION, false);

  return {
    apiBaseUrl,
    publicApiBaseUrl: apiBaseUrl,
    allowLocalApiInProduction,
    isProduction,
    market
  };
}

export function validateRuntimeConfig(config: RuntimeConfig): RuntimeConfigValidation {
  const errors: string[] = [];
  const isChinaVersion = config.market.isChinaVersion;

  if (config.isProduction && config.apiBaseUrl && !isAbsoluteHttpUrl(config.apiBaseUrl)) {
    errors.push(isChinaVersion
      ? "生产环境 API 地址必须是完整的 http(s) 地址，不能使用相对路径。"
      : "Production API base URL must be a full http(s) URL, not a relative path.");
  }

  if (
    config.isProduction
    && config.apiBaseUrl
    && isAbsoluteHttpUrl(config.apiBaseUrl)
    && !config.apiBaseUrl.startsWith("https://")
  ) {
    errors.push(isChinaVersion ? "生产环境 API 地址必须使用 HTTPS。" : "Production API base URL must use HTTPS.");
  }

  if (
    config.isProduction
    && !config.allowLocalApiInProduction
    && config.apiBaseUrl
    && isLocalApiBase(config.apiBaseUrl)
  ) {
    errors.push(isChinaVersion
      ? "生产环境 API 地址不能指向 localhost 或 127.0.0.1，请配置正式后端域名。"
      : "Production API base URL cannot point to localhost or 127.0.0.1. Configure the public API domain.");
  }

  return {
    ok: errors.length === 0,
    errors
  };
}

export function buildApiUrl(path: string) {
  return `${runtimeConfig.apiBaseUrl}${path}`;
}

function readString(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function readBoolean(value: string | boolean | undefined, fallback: boolean) {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value !== "string" || !value.trim()) {
    return fallback;
  }

  const normalized = value.trim().toLowerCase();
  if (["true", "1", "yes", "on"].includes(normalized)) {
    return true;
  }
  if (["false", "0", "no", "off"].includes(normalized)) {
    return false;
  }
  return fallback;
}

function normalizeBaseUrl(value: string) {
  return value.replace(/\/+$/, "");
}

function isLocalApiBase(value: string) {
  try {
    const url = new URL(value);
    return ["localhost", "127.0.0.1", "::1"].includes(url.hostname);
  } catch {
    return false;
  }
}

function isAbsoluteHttpUrl(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function defaultApiBaseUrlForMarket(region: DeploymentRegion) {
  return region === "CN" ? defaultChinaApiBaseUrl : defaultGlobalApiBaseUrl;
}

function detectMarket(browserLocation: string, configuredRegion?: string): RuntimeMarketConfig {
  const region = readDeploymentRegion(configuredRegion);
  if (region) {
    return marketConfigForRegion(region);
  }

  const hostname = readHostname(browserLocation);
  if (hostname.endsWith(".cn")) {
    return marketConfigForRegion("CN");
  }

  return marketConfigForRegion("GLOBAL");
}

function readDeploymentRegion(value: unknown): DeploymentRegion | null {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  const normalized = value.trim().toUpperCase();
  return normalized === "CN" || normalized === "GLOBAL" ? normalized : null;
}

function marketConfigForRegion(region: DeploymentRegion): RuntimeMarketConfig {
  if (region === "CN") {
    return {
      region: "CN",
      paymentChannel: "wechat_pay",
      paymentProviderLabel: "微信支付",
      showWechatLogin: true,
      showGoogleLogin: false,
      showIcp: true,
      isChinaVersion: true,
      isInternationalVersion: false,
      loginPath: "/login",
      generatePath: "/generate"
    };
  }

  return {
    region: "GLOBAL",
    paymentChannel: "stripe",
    paymentProviderLabel: "Stripe",
    showWechatLogin: false,
    showGoogleLogin: true,
    showIcp: false,
    isChinaVersion: false,
    isInternationalVersion: true,
    loginPath: "/login",
    generatePath: "/generate"
  };
}

function readHostname(browserLocation: string) {
  if (!browserLocation) {
    return "";
  }
  try {
    return new URL(browserLocation).hostname.toLowerCase();
  } catch {
    return "";
  }
}

function getBrowserLocation() {
  if (typeof window === "undefined") {
    return "";
  }
  return window.location.href;
}
